<template>
  <div class="annotation-highlighter">
    <!-- 使用模板渲染标注内容 -->
    <span v-for="(segment, index) in contentSegments" :key="index">
      <span
        v-if="segment.type !== 'default'"
        :class="['annotated-text', segment.isActive ? 'active-annotation' : '']"
        :style="segment.style"
        :data-annotation-id="segment.annotationId"
        :data-annotation-type="segment.annotationType"
        @click="handleAnnotationClick(segment.annotationId)"
      >{{ segment.text }}</span>
      <span v-if="segment.type !== 'default' && segment.indexBadge" class="annotation-index-badge" :style="segment.badgeStyle">{{ segment.indexNumber }}</span>
      <span v-else>{{ segment.text }}</span>
    </span>
  </div>
</template>

<script>
export default {
  name: 'AnnotationHighlighter',
  props: {
    content: {
      type: String,
      required: true
    },
    annotations: {
      type: Array,
      default: () => []
    },
    selectedAnnotationId: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {}
  },
  computed: {
    // 创建批注区域映射
    annotationMap() {
      const map = {}
      const processedIds = new Set()

      this.annotations.forEach((annotation, index) => {
        const annotationWithId = { ...annotation, id: index }

        if (annotation.original_content && annotation.original_content.trim()) {
          const key = `content:${annotation.original_content.trim()}`

          if (!map[key]) {
            map[key] = []
          }

          if (!processedIds.has(index)) {
            map[key].push(annotationWithId)
            processedIds.add(index)
          }
        }
      })

      return map
    },

    // 按正文中首次命中的顺序，为可展示批注分配连续编号
    annotationDisplayOrderMap() {
      if (!this.content || this.annotations.length === 0) {
        return {}
      }

      const matchedEntries = []
      const seenKeys = new Set()

      this.annotations.forEach((annotation, index) => {
        const searchText = annotation.original_content && annotation.original_content.trim()
        if (!searchText) {
          return
        }

        const key = `content:${searchText}`
        if (seenKeys.has(key)) {
          return
        }

        const positions = this.findContentPositions(this.content, searchText)
        if (!positions.length) {
          return
        }

        seenKeys.add(key)
        matchedEntries.push({
          key,
          start: positions[0].start,
          index
        })
      })

      matchedEntries.sort((a, b) => {
        if (a.start !== b.start) {
          return a.start - b.start
        }
        return a.index - b.index
      })

      const orderMap = {}
      matchedEntries.forEach((entry, idx) => {
        orderMap[entry.key] = idx + 1
      })

      return orderMap
    },

    // 将内容分割成可渲染的分段数组
    contentSegments() {
      if (!this.content || this.annotations.length === 0) {
        return [{ type: 'default', text: this.content }]
      }

      const content = this.content
      const charMap = new Array(content.length + 1).fill(null).map(() => ({
        annotations: [],
        isActive: false,
        activeType: ''
      }))

      // 收集所有批注的范围
      this.annotations.forEach((annotation, index) => {
        const searchText = annotation.original_content.trim()

        if (searchText) {
          const positions = this.findContentPositions(content, searchText)

          positions.forEach(position => {
            const adjustedStart = this.findWordBoundary(content, position.start, 'backward')
            const adjustedEnd = this.findWordBoundary(content, position.end, 'forward')

            const annotationWithId = { ...annotation, id: index }

            for (let i = adjustedStart; i < adjustedEnd; i++) {
              if (!charMap[i].annotations.some(ann => ann.id === index)) {
                charMap[i].annotations.push(annotationWithId)
              }
            }
          })
        }
      })

      // 分组连续字符
      const segments = []
      let currentSegment = null

      for (let i = 0; i <= content.length; i++) {
        const charInfo = charMap[i]

        let currentType = 'default'
        let isActive = false
        let activeType = ''
        const keys = []

        if (charInfo && charInfo.annotations.length > 0) {
          currentType = charInfo.annotations[0].type
          isActive = charInfo.isActive
          activeType = charInfo.activeType

          const collectedKeys = new Set()

          for (const ann of charInfo.annotations) {
            collectedKeys.add(`content:${ann.original_content}`)
          }

          keys.push(...collectedKeys)
        }

        if (
          !currentSegment ||
          currentSegment.type !== currentType ||
          currentSegment.isActive !== isActive ||
          (isActive && currentSegment.activeType !== activeType)
        ) {
          if (currentSegment) {
            segments.push(currentSegment)
          }

          if (i < content.length) {
            currentSegment = {
              start: i,
              end: i + 1,
              type: currentType,
              isActive: isActive,
              activeType: activeType,
              annotationKeys: keys
            }
          } else {
            currentSegment = null
          }
        } else if (currentSegment) {
          currentSegment.end = i + 1

          for (const key of keys) {
            if (!currentSegment.annotationKeys.includes(key)) {
              currentSegment.annotationKeys.push(key)
            }
          }
        }
      }

      // 转换为渲染所需的分段数组
      const result = []

      for (const segment of segments) {
        const segmentText = content.substring(segment.start, segment.end)

        if (segment.type !== 'default') {
          const formattedKeys = [...new Set(segment.annotationKeys)].sort((a, b) => {
            const orderA = this.annotationDisplayOrderMap[a] || Number.MAX_SAFE_INTEGER
            const orderB = this.annotationDisplayOrderMap[b] || Number.MAX_SAFE_INTEGER
            if (orderA !== orderB) {
              return orderA - orderB
            }
            return a.localeCompare(b)
          })

          let inlineStyle = 'padding: 2px 4px; border-radius: 3px; cursor: pointer; transition: all 0.2s ease;'
          let indexBadge = false
          let indexNumber = ''
          let badgeStyle = ''
          let annotationId = ''

          // 根据批注类型设置背景颜色(移除 border-bottom)
          switch (segment.type) {
            case 'highlight':
              inlineStyle += 'background-color: rgba(103, 194, 58, 0.15);'
              break
            case 'correction':
              inlineStyle += 'background-color: rgba(230, 162, 60, 0.15);'
              break
            case 'suggestion':
              inlineStyle += 'background-color: rgba(245, 108, 108, 0.15);'
              break
          }

          // 处理批注信息
          if (formattedKeys.length > 0) {
            const firstKey = formattedKeys[0]
            const annotations = this.annotationMap[firstKey]
            if (annotations && annotations.length > 0) {
              const annotation = annotations[0]
              annotationId = annotation.id !== undefined ? annotation.id : parseInt(firstKey)

              // 批注序号
              indexBadge = true
              indexNumber = this.annotationDisplayOrderMap[firstKey] || ((annotation.id !== undefined ? annotation.id : parseInt(firstKey)) + 1)

              // 根据批注类型设置不同的文字颜色
              let textColor = ''
              switch (segment.type) {
                case 'highlight':
                  textColor = '#67C23A'
                  break
                case 'correction':
                  textColor = '#E6A23C'
                  break
                case 'suggestion':
                  textColor = '#F56C6C'
                  break
                default:
                  textColor = '#46B4B1'
              }

              badgeStyle = `color: ${textColor}; font-size: 11px; font-weight: 700; margin-left: 2px; vertical-align: super;`
            }
          }

          // 如果当前批注被选中,添加下边框
          if (annotationId === this.selectedAnnotationId) {
            let borderColor = ''
            switch (segment.type) {
              case 'highlight':
                borderColor = 'rgba(103, 194, 58, 0.8)'
                break
              case 'correction':
                borderColor = 'rgba(230, 162, 60, 0.8)'
                break
              case 'suggestion':
                borderColor = 'rgba(245, 108, 108, 0.8)'
                break
            }
            inlineStyle += `border-bottom: 2px solid ${borderColor};`
          }

          result.push({
            type: segment.type,
            text: segmentText,
            isActive: segment.isActive,
            style: inlineStyle,
            annotationId: annotationId,
            annotationType: segment.type,
            indexBadge: indexBadge,
            indexNumber: indexNumber,
            badgeStyle: badgeStyle
          })
        } else {
          result.push({
            type: 'default',
            text: segmentText
          })
        }
      }

      return result
    }
  },
  methods: {
    // 处理批注点击事件
    handleAnnotationClick(annotationId) {
      this.$emit('annotation-click', annotationId)
    },

    // 查找单词边界
    findWordBoundary(text, position, direction) {
      // 不扩展边界,直接使用原始位置
      return position
    },

    // 查找文本片段在内容中的位置
    findContentPositions(content, searchText) {
      if (!searchText || searchText.trim() === '') {
        return []
      }

      const positions = []

      let pos = content.indexOf(searchText)
      while (pos !== -1) {
        positions.push({
          start: pos,
          end: pos + searchText.length
        })
        pos = content.indexOf(searchText, pos + 1)
      }

      return positions
    }
  }
}
</script>

<style scoped>
.annotation-highlighter {
  line-height: 1.7;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

.annotated-text {
  padding: 2px 4px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.annotated-text:hover {
  transform: scale(1.02);
}

/* highlight = 完全覆盖 = 绿色 */
.annotated-text[data-annotation-type='highlight'] {
  background-color: rgba(103, 194, 58, 0.15) !important;
}

.annotated-text[data-annotation-type='highlight']:hover {
  background-color: rgba(103, 194, 58, 0.25) !important;
}

/* correction = 部分覆盖 = 橙色 */
.annotated-text[data-annotation-type='correction'] {
  background-color: rgba(230, 162, 60, 0.15) !important;
}

.annotated-text[data-annotation-type='correction']:hover {
  background-color: rgba(230, 162, 60, 0.25) !important;
}

/* suggestion = 未覆盖 = 红色 */
.annotated-text[data-annotation-type='suggestion'] {
  background-color: rgba(245, 108, 108, 0.15) !important;
}

.annotated-text[data-annotation-type='suggestion']:hover {
  background-color: rgba(245, 108, 108, 0.25) !important;
}

.annotation-index-badge {
  display: inline-block;
}
</style>
